'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// UI lib components
import {
  ActionIcon,
  Button,
  Collapse,
  Dialog,
  Divider,
  Flex,
  Progress,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  rem,
} from '@mantine/core';
import IconInProgress from '@ui-kit/atoms/LoaderIcon';

// UI local components
import ResourceUploadActivity from '../ResourceUploadActivity';
import PermissionBadges from './components/PermissionBadges';

// Store
import {
  uploadingFilesVar,
  cancelAllUploads,
  cleanupAllAbortControllers,
} from '../store/uploadingFiles';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Hooks
import { useParentPermissions } from './hooks/useParentPermissions';
import {
  useUploadProgress,
  formatRemainingTime,
} from './hooks/useUploadProgress';
import { useUploadDestinations } from './hooks/useUploadDestinations';
import { useCancelConfirmation } from './hooks/useCancelConfirmation';

// Types
import { ResourceUploadMenuProps } from './types';

// Assets
import {
  IconChevronDown,
  IconCircleCheck,
  IconX,
  IconLock,
} from '@tabler/icons-react';

// Style
import * as classes from './styles/index.css';
import * as dialogStylesAPI from './styles/dialogStylesAPI.css';
import { palette } from '@app/shared/ui-kit/colors';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const SCROLL_AREA_MAX_HEIGHT = '40vh';
const HEADER_PROGRESS_BAR_WIDTH = 80;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceUploadMenu(_props: ResourceUploadMenuProps) {
  /* ********************************** HOOKS ********************************* */

  // Upload progress state
  const {
    resourceUploads,
    statistics,
    uploadProgression,
    totalItems,
    isSavingToServer,
    isUploading,
    isComplete,
  } = useUploadProgress();

  // Upload destinations
  const { uploadParentId, hasMultipleDestinations } = useUploadDestinations();

  // Cancel confirmation
  const { showCancelConfirmation } = useCancelConfirmation();

  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Ref to track if menu has been opened (avoids re-opening after manual close)
  const hasBeenOpenedRef = useRef(false);

  // Store
  const workspace = useWorkspace();

  // Locale
  const {
    t,
    i18n: { language },
  } = useTranslation(['Resource', 'Common']);

  /* ***************************** LOCAL VARIABLES **************************** */

  const showPermissions = isComplete;

  // Fetch parent permissions only when upload is complete (deferred to avoid unnecessary API calls)
  const {
    groupPermissions,
    hasPermissions,
    isLoading: parentLoading,
    isAtRoot,
    hasError: parentError,
  } = useParentPermissions(workspace.id, uploadParentId, {
    skip: !showPermissions,
  });

  /* ******************************* SIDE EFFECTS ****************************** */

  // Auto-open when uploads start (only if not manually closed before)
  useEffect(() => {
    if (resourceUploads.length > 0 && !hasBeenOpenedRef.current) {
      hasBeenOpenedRef.current = true;
      setIsOpen(true);
    }
    // Reset ref when all uploads are cleared
    if (resourceUploads.length === 0) {
      hasBeenOpenedRef.current = false;
    }
  }, [resourceUploads.length]);

  // Cleanup AbortControllers on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      cleanupAllAbortControllers();
    };
  }, []);

  /* ******************************** CALLBACKS ******************************* */

  function closeResourceUploadMenu() {
    // If uploads are in progress, show confirmation prompt
    if (isUploading) {
      showCancelConfirmation(() => {
        cancelAllUploads();
        setIsOpen(false);
      });
      return;
    }

    // If not uploading, just close and reset
    uploadingFilesVar({});
    setIsOpen(false);
  }

  /* ******************************** RENDERING ******************************* */

  if (resourceUploads.length === 0) {
    return null;
  }

  return (
    <Dialog
      classNames={{ ...dialogStylesAPI }}
      bg="black"
      c="white"
      w={400}
      position={{ bottom: 8, right: 8 }}
      opened={isOpen}
      withBorder={false}
    >
      <Stack gap={0}>
        {/* Compact Header - always visible */}
        <Flex
          align="center"
          justify="space-between"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Flex align="center" gap={12} style={{ flex: 1 }}>
            {isComplete ? (
              <IconCircleCheck size={20} color={palette.blackroomGreen} />
            ) : (
              <IconInProgress loading size={20} />
            )}
            <Text size="sm" fw={500}>
              {isComplete
                ? t('Resource:ResourceUploadActivity.complete', {
                    count: totalItems,
                  })
                : isSavingToServer
                  ? t('Resource:ResourceUploadActivity.saving')
                  : t('Resource:ResourceUploadActivity.uploading', {
                      count: totalItems,
                    })}
            </Text>
          </Flex>

          {/* Progress bar in header when uploading (collapsed) */}
          {!isExpanded && isUploading && (
            <Progress
              value={uploadProgression}
              size="sm"
              w={HEADER_PROGRESS_BAR_WIDTH}
              style={{ marginRight: 12 }}
            />
          )}

          {/* Permissions indicator when complete (collapsed) */}
          {!isExpanded && isComplete && (
            <Tooltip
              label={t('Resource:DialogUploadPermissions.seePermissions')}
            >
              <Flex
                align="center"
                gap={4}
                style={{ marginRight: 12, cursor: 'pointer' }}
              >
                <IconLock size={16} color={palette.blackroomBlue} />
              </Flex>
            </Tooltip>
          )}

          <Flex gap="xs">
            <ActionIcon size={22} variant="transparent">
              <IconChevronDown
                className={isExpanded ? classes.icon : classes.rotateChevron}
              />
            </ActionIcon>

            <ActionIcon
              size={22}
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                closeResourceUploadMenu();
              }}
            >
              <IconX className={classes.icon} />
            </ActionIcon>
          </Flex>
        </Flex>

        {/* Expandable Details */}
        <Collapse in={isExpanded}>
          <Stack gap={10} w="100%" pt={8}>
            {/* Progress details when uploading */}
            {isUploading && (
              <Stack gap={8} px={4}>
                <Flex w="100%" align="center" gap={16}>
                  <Progress w="calc(100% - 50px)" value={uploadProgression} />
                  <Text size={rem(12)}>{Math.floor(uploadProgression)}%</Text>
                </Flex>
                {statistics.remainingTime && (
                  <Text size={rem(11)} c="dimmed">
                    {t('Resource:ResourceUploadActivity.remainingTime', {
                      time: formatRemainingTime(
                        language,
                        statistics.remainingTime,
                      ),
                    })}
                  </Text>
                )}
              </Stack>
            )}

            {/* File list */}
            <ScrollArea.Autosize
              mah={SCROLL_AREA_MAX_HEIGHT}
              className={classes.scrollArea}
            >
              <ResourceUploadActivity resourceUploads={resourceUploads} />
            </ScrollArea.Autosize>

            {/* Cancel all button - shown when uploading */}
            {isUploading && (
              <Button
                variant="subtle"
                color="red"
                size="xs"
                onClick={() =>
                  showCancelConfirmation(() => {
                    cancelAllUploads();
                    setIsOpen(false);
                  })
                }
              >
                {t('Common:cta.cancelAll')}
              </Button>
            )}

            {/* Permissions Section - shown when upload is complete */}
            {showPermissions && (
              <>
                <Divider color="gray.7" />
                <Stack gap={8}>
                  <Text size="xs" fw={500}>
                    {t('Resource:DialogUploadPermissions.permissionsLabel')}
                  </Text>
                  <PermissionBadges
                    isLoading={parentLoading}
                    hasError={parentError}
                    hasMultipleDestinations={hasMultipleDestinations}
                    isAtRoot={isAtRoot}
                    hasPermissions={hasPermissions}
                    groupPermissions={groupPermissions}
                  />
                  <Text size="xs" c="dimmed" fs="italic">
                    {t('Resource:DialogUploadPermissions.adminReminder')}
                  </Text>
                </Stack>
              </>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Dialog>
  );
}

export default ResourceUploadMenu;
